import { CommonModule } from '@angular/common';
import { HttpResponse } from '@angular/common/http';
import { Component, input, output, signal } from '@angular/core';
import { Router } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { HeadlessService } from '@src/app/core/services/headless/headless.service';
import { WebformService } from '@src/app/core/services/webform/webform.service';
import { downloadBlob } from '@src/app/core/utils/api.utils';
import { formatDate } from '@src/app/core/utils/date.utils';
import { MessageService } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { finalize, Observable } from 'rxjs';
import {
  CIRCULAR_CHECK_TYPES,
  COMMERCIAL_TYPES,
  CONTRACT_NAMES,
  DOMESTIC_TYPES,
  EUROPEAN_DESK_TYPES,
  POSEEDOR_FINAL_TYPES,
} from '../../amd-contracts.constants';

interface ContractTypeInfo {
  name: string;
  hasCertificate: boolean;
  service?: () => Observable<HttpResponse<Blob>>;
}

@Component({
  selector: 'app-contract-card',
  imports: [CommonModule, ButtonModule, TranslateModule],
  templateUrl: './contract-card.component.html',
  styleUrl: './contract-card.component.scss',
})
export class ContractCardComponent {
  /** Nombre del contrato (ej: "Adhesión Ecoembes Domésticos") */
  readonly name = input.required<string>();

  /** Código del contrato */
  readonly tipoId = input.required<string>();

  /** Fecha de inicio del contrato (Date) */
  readonly startDate = input<Date | null>();

  /** Mostrar (true) u ocultar (false) el botón para solicitar el certificado */
  readonly showCertificateButton = input<boolean>(true);

  /** Mostrar (true) u ocultar (false) el botón de solicitar baja */
  readonly showDeactivateButton = input<boolean>(true);

  /** Evento para cerrar el diálogo antes de navegar */
  readonly closeDialog = output<void>();

  readonly formatDate = formatDate;

  loadingRequestSigned = signal(false);
  loadingRequestCertificate = signal(false);
  loadingDeactivate = signal(false);

  constructor(
    private messageService: MessageService,
    private translate: TranslateService,
    private webformService: WebformService,
    private headlessService: HeadlessService,
    private router: Router,
  ) {}

  /**
   * Determina el tipo de contrato basado en el código del contrato
   */
  private getContractType(): ContractTypeInfo {
    const tipoId = this.tipoId();

    // Contratos con servicio de descarga directa
    if (DOMESTIC_TYPES.includes(tipoId as any)) {
      return {
        name: CONTRACT_NAMES.DOMESTIC,
        hasCertificate: true,
        service: () => this.headlessService.getCertificadoDomestico(),
      };
    }

    if (COMMERCIAL_TYPES.includes(tipoId as any)) {
      return {
        name: CONTRACT_NAMES.COMMERCIAL,
        hasCertificate: true,
        service: () => this.headlessService.getCertificadoComercial(),
      };
    }

    // Contratos sin certificado
    if (CIRCULAR_CHECK_TYPES.includes(tipoId as any)) {
      return { name: CONTRACT_NAMES.CIRCULAR_CHECK, hasCertificate: false };
    }

    if (EUROPEAN_DESK_TYPES.includes(tipoId as any)) {
      return { name: CONTRACT_NAMES.EUROPEAN_DESK, hasCertificate: false };
    }

    if (POSEEDOR_FINAL_TYPES.includes(tipoId as any)) {
      return { name: CONTRACT_NAMES.POSEEDOR_FINAL, hasCertificate: false };
    }

    // Fallback basado en nombre traducido (fase 2 usará códigos definitivos)
    const name = this.name();
    if (name.includes('businessPlans') || name.includes('empresariales')) {
      return {
        name: CONTRACT_NAMES.BUSINESS_PLANS,
        hasCertificate: false,
      };
    }

    if (name.includes('producer') || name.includes('Productores')) {
      return { name: CONTRACT_NAMES.PRODUCER, hasCertificate: false };
    }

    // Fallback por defecto
    return { name: CONTRACT_NAMES.DOMESTIC, hasCertificate: true };
  }

  onRequestSigned(): void {
    const contractType = this.getContractType();
    const form = {
      proceso: 'Contratos',
      motivo: `${contractType.name}. Descarga contrato firmado`,
    };

    this.loadingRequestSigned.set(true);
    this.webformService
      .createGenericTask(form)
      .pipe(finalize(() => this.loadingRequestSigned.set(false)))
      .subscribe({
        next: (data) => {
          if (data.sid) {
            this.messageService.add({
              severity: 'success',
              summary: this.translate.instant(
                'accountManagement.dialog.contracts.toast.requestSigned.summary',
              ),
              detail: this.translate.instant(
                'accountManagement.dialog.contracts.toast.requestSigned.detail',
              ),
            });
          }
        },
      });
  }

  onRequestCertificate(): void {
    const contract = this.getContractType();
    const form = {
      proceso: 'Contratos',
      motivo: `${contract.name}. Descarga certificado de adhesión`,
    };
    if (contract.hasCertificate) {
      this.loadingRequestCertificate.set(true);
      contract.service!()
        .pipe(finalize(() => this.loadingRequestCertificate.set(false)))
        .subscribe({
          next: (response) => {
            downloadBlob(response);
            this.messageService.add({
              severity: 'success',
              life: 5000,
              summary: this.translate.instant(
                'requests.new.certificates.toast.successDownload.summary',
              ),
              detail: this.translate.instant(
                'requests.new.certificates.toast.successDownload.detail',
              ),
            });
          },
          error: () => {
            this.requestGenericTask(form);
          },
        });
    } else {
      this.requestGenericTask(form);
    }
  }
  requestGenericTask(form: any): void {
    this.loadingRequestCertificate.set(true);
    this.webformService
      .createGenericTask(form)
      .pipe(finalize(() => this.loadingRequestCertificate.set(false)))
      .subscribe({
        next: (data) => {
          if (data.sid) {
            this.messageService.add({
              severity: 'success',
              life: 5000,
              summary: this.translate.instant(
                'requests.new.certificates.toast.successRequest.summary',
              ),
              detail: this.translate.instant(
                'requests.new.certificates.toast.successRequest.detail',
              ),
            });
          }
        },
      });
  }

  onDeactivate(): void {
    const contractType = this.getContractType();

    // Cerrar el diálogo antes de navegar
    this.closeDialog.emit();

    // TimeOut para esperar que el modal de los contratos y gestion de cuente ya se haya cerrado
    setTimeout(() => {
      this.router.navigate(['/requests/new-request/operational'], {
        queryParams: {
          contract_type: contractType.name,
          motivo: 'contratos',
          request_type: 'baja',
        },
      });
    }, 100);
  }
}
